"use client";

import * as React from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { ptBR } from "react-day-picker/locale";

import { formatIsoDate, parseIsoDate, toIsoDate } from "../../utils/date";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "./utils";

interface DatePickerProps {
  id?: string;
  value?: string | null;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  className?: string;
  "aria-label"?: string;
}

function DatePicker({
  id,
  value,
  onValueChange,
  placeholder = "Selecione uma data",
  disabled = false,
  required = false,
  min,
  max,
  className,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [yearSelectionOpen, setYearSelectionOpen] = React.useState(false);
  const selected = parseIsoDate(value);
  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);
  const currentDate = new Date();
  const firstMonth = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    : new Date(currentDate.getFullYear() - 100, 0, 1);
  const lastMonth = maxDate
    ? new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
    : new Date(currentDate.getFullYear() + 50, 11, 1);
  const [month, setMonth] = React.useState(
    selected ?? minDate ?? currentDate,
  );
  const [yearPageStart, setYearPageStart] = React.useState(
    Math.floor((selected ?? minDate ?? currentDate).getFullYear() / 12) * 12,
  );

  React.useEffect(() => {
    const nextMonth = selected ?? minDate;
    if (nextMonth) {
      setMonth(nextMonth);
      setYearPageStart(Math.floor(nextMonth.getFullYear() / 12) * 12);
    }
  }, [value, min]);

  const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();
  const canNavigatePrevious = monthIndex(month) > monthIndex(firstMonth);
  const canNavigateNext = monthIndex(month) < monthIndex(lastMonth);

  const navigateMonth = (amount: number) => {
    const nextMonth = new Date(month.getFullYear(), month.getMonth() + amount, 1);
    if (monthIndex(nextMonth) < monthIndex(firstMonth)) return;
    if (monthIndex(nextMonth) > monthIndex(lastMonth)) return;
    setMonth(nextMonth);
  };

  const openYearSelection = () => {
    setYearPageStart(Math.floor(month.getFullYear() / 12) * 12);
    setYearSelectionOpen(true);
  };

  const selectYear = (year: number) => {
    let nextMonth = new Date(year, month.getMonth(), 1);
    if (monthIndex(nextMonth) < monthIndex(firstMonth)) nextMonth = firstMonth;
    if (monthIndex(nextMonth) > monthIndex(lastMonth)) nextMonth = lastMonth;
    setMonth(nextMonth);
    setYearSelectionOpen(false);
  };

  const monthName = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
  }).format(month);
  const formattedMonthLabel = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${month.getFullYear()}`;
  const years = Array.from({ length: 12 }, (_, index) => yearPageStart + index);
  const firstYearPage = Math.floor(firstMonth.getFullYear() / 12) * 12;
  const lastYearPage = Math.floor(lastMonth.getFullYear() / 12) * 12;

  const selectDate = (date?: Date) => {
    if (!date) return;
    onValueChange(toIsoDate(date));
    setOpen(false);
  };

  const selectToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (minDate && today < minDate) return;
    if (maxDate && today > maxDate) return;
    setMonth(today);
    selectDate(today);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setYearSelectionOpen(false);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-required={required}
          className={cn(
            "h-10 w-full justify-between rounded-lg bg-input-background px-3 text-left font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span>{selected ? formatIsoDate(value) : placeholder}</span>
          <CalendarDays className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[320px] overflow-hidden rounded-2xl border-border/80 p-0 shadow-xl"
      >
        <div className="flex h-14 items-center justify-between px-3 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 rounded-full"
            disabled={yearSelectionOpen ? yearPageStart <= firstYearPage : !canNavigatePrevious}
            onClick={() => yearSelectionOpen
              ? setYearPageStart((year) => year - 12)
              : navigateMonth(-1)}
            aria-label={yearSelectionOpen ? "Anos anteriores" : "Mês anterior"}
          >
            <ChevronLeft className="size-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="rounded-lg px-3 font-semibold capitalize"
            onClick={() => yearSelectionOpen
              ? setYearSelectionOpen(false)
              : openYearSelection()}
          >
            {yearSelectionOpen
              ? `${yearPageStart}–${yearPageStart + 11}`
              : formattedMonthLabel}
            <ChevronDown
              className={cn("size-3.5 transition-transform", yearSelectionOpen && "rotate-180")}
            />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 rounded-full"
            disabled={yearSelectionOpen ? yearPageStart >= lastYearPage : !canNavigateNext}
            onClick={() => yearSelectionOpen
              ? setYearPageStart((year) => year + 12)
              : navigateMonth(1)}
            aria-label={yearSelectionOpen ? "Próximos anos" : "Próximo mês"}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>

        {yearSelectionOpen ? (
          <div className="grid grid-cols-3 gap-2 px-4 pb-4 pt-2">
            {years.map((year) => {
              const disabledYear = year < firstMonth.getFullYear() || year > lastMonth.getFullYear();
              const selectedYear = year === month.getFullYear();
              return (
                <Button
                  key={year}
                  type="button"
                  variant={selectedYear ? "default" : "ghost"}
                  disabled={disabledYear}
                  className="h-10 rounded-xl"
                  onClick={() => selectYear(year)}
                >
                  {year}
                </Button>
              );
            })}
          </div>
        ) : (
          <Calendar
            mode="single"
            locale={ptBR}
            selected={selected}
            month={month}
            onMonthChange={setMonth}
            startMonth={firstMonth}
            endMonth={lastMonth}
            hideNavigation
            className="px-4 pb-4 pt-0"
            classNames={{ month_caption: "hidden" }}
            disabled={(date) =>
              Boolean((minDate && date < minDate) || (maxDate && date > maxDate))
            }
            onSelect={selectDate}
            autoFocus
          />
        )}
        <div className="flex items-center justify-between border-t bg-muted/20 px-3 py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!selected}
            onClick={() => {
              onValueChange("");
              setOpen(false);
            }}
          >
            Limpar
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={selectToday}>
            Hoje
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
export type { DatePickerProps };
